import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
    createColumnHelper,
} from "@tanstack/react-table";
import { saveAs } from 'file-saver';
import toast, { Toaster } from 'react-hot-toast';
import {useNavigate} from "react-router-dom";

const ChannelManagerList = () => {
    const [channelManagerData, setChannelManagerData] = useState([]);
    const [searchFilter, setSearchFilter] = useState("");
    const [newEmail, setNewEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedChannelManagerEmail, setSelectedChannelManagerEmail] = useState("");
    const [updatePassword, setUpdatePassword] = useState("");

    const navigate = useNavigate();

    const columnHelper = createColumnHelper();

    const columns = useMemo(
        () => [
            columnHelper.accessor("_id", {
                header: () => "Channel Manager ID",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("adminUsername", {
                header: () => "Email ID",
                cell: (info) => info.getValue(),
            }),
            columnHelper.accessor("password", {
                header: () => "Password",
                cell: (info) => "****",
            }),
            columnHelper.accessor("menu", {
                header: () => "Actions",
                cell: (info) => (
                    <>
                        <button onClick={() => openPasswordModal(info.row.original.adminUsername)} className="btn btn-primary">
                            <span className="text-bold text-xl" style={{filter: "grayscale(100%)"}}>🔐</span>
                            Update Password
                        </button>
                        <button onClick={() => navigate(`/tasksList/all/?channelManager=${info.row.original._id}`)} className="ml-22 btn btn-secondary">  <span className="text-bold text-xl"> ↸ </span>View Tasks</button>
                    </>
                ),
            }),
        ],
        []
    );

    const table = useReactTable({
        data: channelManagerData,
        columns,
        state: {
            globalFilter: searchFilter,
        },
        onGlobalFilterChange: setSearchFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const getChannelManagerList = async () => {
        const userToken = localStorage.getItem("user-token");

        const headers = {
            "authorization": userToken,
        };
        let bUrl = `/admin/channelManagers`; //  Endpoint for channel managers

        try {
            const response = await axios.get(bUrl, {
                headers: headers,
                params: { search: searchFilter }, // Pass the search query
            });

            setChannelManagerData(response.data.data); // Assuming the API returns { data: [channelManagers] }
        } catch (error) {
            console.error("Error fetching channel manager data:", error);
            toast.error("Error fetching channel manager data.");
        }
    };

    const createChannelManager = async () => {
        const userToken = localStorage.getItem("user-token");
        const headers = {
            "authorization": userToken,
        };

        try {
            await axios.post(`/admin/channelManagers`, {
                email: newEmail,
                password: newPassword,
            }, { headers });
            // Refresh the channel manager list after successful creation
            getChannelManagerList();
            // Clear the input fields
            setNewEmail("");
            setNewPassword("");
            toast.success("Channel Manager created successfully!");
        } catch (error) {
            console.error("Error creating channel manager:", error);
            toast.error("Failed to create channel manager.");
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) {
            toast.error("Please select a CSV file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const csvText = e.target.result;
            const lines = csvText.split('\n'); // Split by lines
            const headers = lines[0].split(','); // Assuming comma-separated values
            // Trim whitespace from headers
            const trimmedHeaders = headers.map(header => header.trim());

            for (let i = 1; i < lines.length; i++) { // Skip the header row
                const data = lines[i].split(',');
                // Trim whitespace from data elements
                const trimmedData = data.map(dataItem => dataItem.trim());
                if (data.length !== trimmedHeaders.length) {
                    console.warn(`Skipping line ${i + 1} due to incorrect number of columns.`);
                    continue; // Skip lines with incorrect column count
                }

                const channelManagerData = {};
                for (let j = 0; j < trimmedHeaders.length; j++) {
                    channelManagerData[trimmedHeaders[j]] = trimmedData[j];
                }
                // check for email and password existance to push to server
                if(channelManagerData.emailId && channelManagerData.password){
                    try {
                        const userToken = localStorage.getItem("user-token");
                        const headers = {
                            "authorization": userToken,
                        };
                        await axios.post(`/admin/channelManagers`, {
                            email: channelManagerData.emailId,
                            password: channelManagerData.password,
                        }, { headers });
                        toast.success(`Channel Manager ${channelManagerData.emailId} created successfully!`);
                    } catch (error) {
                        console.error("Error creating channel manager:", error);
                        toast.error(`Failed to create channel manager ${channelManagerData.emailId}.`);
                    }
                } else {
                    toast.error(`Please add email and password to row ${i+1}`);
                }

            }
            getChannelManagerList();
        };
        reader.onerror = () => {
            toast.error("Error reading CSV file.");
        };
        reader.readAsText(file);
    };

    const openPasswordModal = (email) => {
        setSelectedChannelManagerEmail(email);
        setUpdatePassword(""); // Clear previous password
        setShowPasswordModal(true);
    };

    const closePasswordModal = () => {
        setShowPasswordModal(false);
    };

    const updateChannelManagerPassword = async () => {
        const userToken = localStorage.getItem("user-token");
        const headers = {
            "authorization": userToken,
        };
        try {
            console.log("we are going to send the request")
            await axios.put(`/admin/channelManagers`, {
                email: selectedChannelManagerEmail,
                password: updatePassword,
            }, { headers });
            getChannelManagerList();
            closePasswordModal();
            toast.success("Password updated successfully!");
        } catch (error) {
            console.error("Error updating password:", error);
            toast.error("Failed to update password.");
        }
    };

    useEffect(() => {
        getChannelManagerList();
    }, [searchFilter]); //  Fetch data when the component mounts and when searchFilter changes

    const downloadSampleCSV = () => {
        const headers = ["emailId", "password"];
        const sampleData = [
            ["example1@example.com", "password123"],
            ["example2@example.com", "pass456"]
        ];

        let csvContent = headers.join(",") + "\n";
        csvContent += sampleData.map(row => row.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "sampleChannelManagers.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={"flex"}>
            <div className="w-[90vw] ml-[7vw] mt-[2vh] mr-0 overflow-auto rounded-lg shadow-md">
                <h1 className="text-2xl font-bold mb-4">Channel Managers</h1>

                {/* Creation Form */}
                <div className="flex flex-col m-auto items-center p-6 rounded-lg shadow-md bg-white w-full max-w-2xl">
                    <h2 className="text-lg font-semibold mb-4 text-gray-800">Create Channel Manager</h2>

                    <div className="w-full mb-4">
                        <input
                            type="email"
                            placeholder="Email Address"
                            className="input input-bordered w-full mb-2"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                        />

                        <input
                            type="password"
                            placeholder="Password"
                            className="input input-bordered w-full"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-center w-full mb-4">
                        <button className="btn btn-primary" onClick={createChannelManager}>Create Channel Manager
                        </button>
                    </div>


                    <div className="w-full flex flex-col items-center">
                        <div className="mb-2">
                            <input type="file" className="file-input file-input-bordered file-input-xs"
                                   onChange={handleFileUpload}/>
                        </div>
                        <div>
                            <a onClick={downloadSampleCSV}
                               className="text-sm text-blue-500 hover:underline">Download Sample CSV</a>
                        </div>


                    </div>
                </div>
                <div className="p-4">
                    <input
                        type="text"
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                        placeholder="Search by Channel Manager ID, Name, Email..."
                        className="input input-bordered w-full"
                    />
                </div>
                <div className="overflow-x-auto">
                    <table className="table w-full">
                        <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th
                                        key={header.id}
                                        className={`text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.column.getCanSort() ? "cursor-pointer select-none" : ""}`}
                                        onClick={header.column.getToggleSortingHandler()}
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        {{
                                            asc: " ↑",
                                            desc: " ↓",
                                        }[header.column.getIsSorted()] ?? null}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                className="hover:bg-gray-100 cursor-pointer transition-colors duration-200"
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                <div className="join p-4">
                    <button
                        className="join-item btn"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<<"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        {"<"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        {">"}
                    </button>
                    <button
                        className="join-item btn"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        {">>"}
                    </button>
                    <span style={{alignItems: 'center'}} className="flex input input-bordered join-item">
            Page
                        {' '}
                        <strong>
              {table.getState().pagination.pageIndex + 1} of{' '}
                            {table.getPageCount()}
            </strong>
          </span>
                    <span className="flex items-center gap-1">

            <input
                type="number"
                min={1}
                max={table.getPageCount()}
                defaultValue={table.getState().pagination.pageIndex + 1}
                onChange={(e) => {
                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                    table.setPageIndex(page);
                }}
                className="input input-bordered w-24"
            />
          </span>
                    <select
                        className="select select-bordered"
                        value={table.getState().pagination.pageSize}
                        onChange={(e) => {
                            table.setPageSize(Number(e.target.value));
                        }}
                    >
                        {[10, 20, 30, 40, 50].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Update Password Modal */}
                {showPasswordModal && (
                    <div className="modal modal-open">
                        <div className="modal-box">
                            <h3 className="font-bold text-lg">Update Password</h3>
                            <div className="py-4">
                                <label className="label">
                                    <span className="label-text">Email</span>
                                </label>
                                <input type="email" placeholder="Email" className="input input-bordered w-full" value={selectedChannelManagerEmail} disabled />
                                <label className="label">
                                    <span className="label-text">New Password</span>
                                </label>
                                <input type="password" placeholder="New Password" className="input input-bordered w-full" value={updatePassword} onChange={(e) => setUpdatePassword(e.target.value)} />
                            </div>
                            <div className="modal-action">
                                <button className="btn" onClick={closePasswordModal}>Cancel</button>
                                <button className="btn btn-primary" onClick={updateChannelManagerPassword}>Update</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChannelManagerList;
